import React from 'react';
import { Upload, Linkedin, Github, Mail, Copy, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { integrationList } from '@/data/integrations';
import { offeringList } from '@/data/offerings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function Footer() {
  return (
    <footer className="border-t border-border bg-card py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <a href="/" className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-[10px] bg-gradient-to-br from-brand-from to-brand-to flex items-center justify-center shadow-glow">
                <Upload className="w-4 h-4 text-white" />
              </div>
              <span className="text-[17px] font-extrabold text-foreground tracking-tight">Upload to URL</span>
            </a>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Upload files and get instant public URLs. API-first file hosting for developers and teams.
            </p>

            <TooltipProvider delayDuration={200}>
              <div className="mb-8 flex flex-wrap items-center gap-4">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img src="/assets/images/aws-logo.png" alt="AWS Logo" className="h-10 hover:scale-105 transition-transform cursor-default opacity-80 hover:opacity-100 transition-opacity" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-brand text-brand-foreground border-none shadow-card font-medium">
                    <p>Powered by AWS Infrastructure</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <img src="/assets/images/GDPR_Blue.png" alt="GDPR Compliant" className="h-10 hover:scale-105 transition-transform cursor-default opacity-80 hover:opacity-100 transition-opacity" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-brand text-brand-foreground border-none shadow-card font-medium">
                    <p>Fully GDPR Compliant</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>

            <div className="mt-8 mb-6 flex flex-wrap gap-4 items-center">
              <a href="https://launchigniter.com/product/upload-to-url?ref=badge-upload-to-url" target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-[1.02] transition-transform">
                <img src="https://launchigniter.com/api/badge/upload-to-url?theme=light" alt="Featured on LaunchIgniter" width="212" height="55" className="h-11 w-auto rounded-lg shadow-soft" />
              </a>
              <a href="https://twelve.tools" target="_blank" rel="noopener noreferrer" className="inline-block hover:scale-[1.02] transition-transform">
                <img src="https://twelve.tools/badge0-light.svg" alt="Featured on Twelve Tools" width="200" height="54" className="h-11 w-auto rounded-lg shadow-soft" />
              </a>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/company/upload-to-url" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-brand transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://github.com/Upload-to-URL" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="text-muted-foreground hover:text-brand transition-colors">
                  <Github className="w-5 h-5" />
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>support@uploadtourl.com</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('support@uploadtourl.com');
                    toast.success('Email address copied to clipboard');
                  }}
                  className="p-1.5 ml-1 rounded-md text-muted-foreground hover:text-brand hover:bg-brand-tint transition-colors"
                  title="Copy email address"
                  aria-label="Copy email address"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4">Product</h3>
            <ul className="space-y-2.5">
              <li><a href="/api-docs" className="text-sm text-muted-foreground hover:text-brand transition-colors">API Documentation</a></li>
              <li><a href="/integrations" className="text-sm text-muted-foreground hover:text-brand transition-colors">Integrations</a></li>
              <li><a href="/pricing" className="text-sm text-muted-foreground hover:text-brand transition-colors">Pricing</a></li>
              <li><Link to="/mcp-server" className="text-sm text-muted-foreground hover:text-brand transition-colors">MCP Server</Link></li>
              <li><Link to="/google-drive-direct-link-generator" className="text-sm text-muted-foreground hover:text-brand transition-colors">Google Drive Public Link Generator</Link></li>
              <li><Link to="/faq" className="text-sm text-muted-foreground hover:text-brand transition-colors">FAQ</Link></li>
              <li><a href="https://uploadtourl.hyperping.app/" target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground hover:text-brand transition-colors">Status</a></li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4">Solutions</h3>
            <ul className="space-y-2.5">
              {offeringList.map((offering) => (
                <li key={offering.slug}>
                  <Link to={`/${offering.slug}`} className="text-sm text-muted-foreground hover:text-brand transition-colors">
                    {offering.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Integrations */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4">Integrations</h3>
            <ul className="space-y-2.5">
              {integrationList.map((item) => (
                <li key={item.slug}>
                  <a
                    href={`/${item.slug}`}
                    className="text-sm text-muted-foreground hover:text-brand transition-colors inline-flex items-center gap-2 group"
                  >
                    <div className="w-5 h-5 rounded bg-muted flex items-center justify-center overflow-hidden border border-border group-hover:border-brand/30 transition-colors">
                      <img
                        src={item.logoPath}
                        alt={item.name}
                        className="w-3 h-3 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    {item.name}
                    {item.status === 'coming-soon' && (
                      <span className="text-[10px] text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 rounded-full px-1.5 py-0.5 leading-none">Soon</span>
                    )}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <a
                  href="/integrations"
                  className="text-xs font-semibold text-muted-foreground hover:text-brand transition-colors flex items-center gap-1 group"
                >
                  View all Integrations
                  <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-bold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2.5">
              <li><a href="/security" className="text-sm text-success font-semibold hover:opacity-80 transition-opacity">Security & Trust</a></li>
              <li><a href="/privacy" className="text-sm text-muted-foreground hover:text-brand transition-colors">Privacy Policy</a></li>
              <li><a href="/terms" className="text-sm text-muted-foreground hover:text-brand transition-colors">Terms & Conditions</a></li>
              <li><a href="/refund" className="text-sm text-muted-foreground hover:text-brand transition-colors">Refund Policy</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-border pt-8 flex text-center justify-center">
          <span className="text-sm text-muted-foreground">
            &copy; 2026 Upload to URL. All rights reserved.
          </span>
        </div>
      </div>
    </footer>
  );
}
